import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { MainLayout } from './components/layout';
import { ImmersiveThemeProvider } from './contexts/ImmersiveThemeContext';
import { InstallPrompt } from './components/pwa/InstallPrompt';
import FuturisticLayoutGallery from './components/theming/FuturisticLayoutGallery';
import { 
  Dashboard, 
  Login, 
  AdminLogin,
  Accounts, 
  Transactions, 
  Settings, 
  VirtualAccounts,
  Crypto,
  DefiPools,
  Profile,
  AdminTenants, 
  AdminUsers, 
  AdminPermissions, 
  AuditLogs,
  AdminCostsDashboard,
  AdminPartners,
  AdminFees,
  AdminManagers,
  AdminEntities,
  AdminBankConnections,
  AdminFeeRules,
  ProviderTemplates,
  ProviderInstances,
  Monitoring,
  TemplateWizard,
  ResourceConfig,
  FeeConfig,
  TestConsole,
  RevenueDashboard,
  TenantFeeConfig,
  TenantRevenueDashboard,
  ManagerDashboard,
  ManagerApprovals,
  ManagerCommissions,
  ManagerUsers,
  UserLimits,
  UserMyApprovals,
  BenefitPlans,
  BenefitSubscriptions,
  BenefitsDashboard,
  AdminSetup,
  AdminReports,
  AdminMarketplace,
  EconomicGroups,
} from './pages';
import {
  SuperAdminDashboard,
  SuperAdminTenants,
  SuperAdminProviders,
  SuperAdminProviderTemplates,
  SuperAdminUsers,
  PlatformFeeConfig,
  FeeHealthCheck,
  ProviderCosts,
  TenantRevenueList,
  TokenManagement,
  ThemeEditor,
  ThemeEditorAdvanced,
  PlatformModules,
  FinancialSecuritySettings,
  FinancialHealthDashboard,
  // Tokenization Super Admin pages
  AssetTypesManagement,
  TokenizationDashboard,
  OracleConfiguration,
  ComplianceRulesConfig,
  TenantCategoryAccess,
  // Company PJ Super Admin pages
  CompanyListAll,
  CompanyDetailAll,
} from './pages/super-admin';
import { 
  KycQueue, 
  KycReview, 
  KycDashboard, 
  KycSettings, 
  KycAnalystManagement, 
  KycTemplates, 
  KycReports,
  ComplianceQueue,
  AssetReview,
  FacialVerificationRules,
  FacialConfigPage,
  TenantModules,
  AdvancedSecurityConfig,
  SecurityAuditLogs,
  SystemHealthDashboard,
  ImmersiveThemes,
  AnypayDashboard,
  CompanyRegistration,
  MembershipFeesPage,
  FinancialOverviewPage,
  FinancialSummaryPage,
  SecurityConfigPage,
  ReconciliationPage,
  ReportsHubPage,
  ConsolidatedReportPage,
  PerCustomerReportPage,
  FeesMembershipReportPage,
  DetailedTransactionsReportPage,
  // Company PJ Admin pages
  CompanyList,
  CompanyDetail,
} from './pages/admin';
import { MedQueue, MedReview, PldDashboard, PldAlertList, PldAlertDetail, PldRuleList, PldRuleCreate, PldConfig, CoafDashboard, CoafCommunicationList, CoafCommunicationDetail, CoafCommunicationCreate, ComplianceConfig } from './pages/admin/compliance';
import {
  CreditDashboard,
  CreditPolicies,
  CreditProducts,
  LoanRequests,
  ActiveLoans,
  CollectionRules,
  CreditReports,
} from './pages/admin/credit';
import {
  OpenFinanceConfig,
  OpenFinanceMonitor,
} from './pages/admin/open-finance';
import {
  CardIssuingConfig,
  CardManagement,
} from './pages/admin/cards';
import {
  InvestmentProductsAdmin,
  InvestmentConfig,
} from './pages/admin/investments';
import {
  ContractsDashboard,
  TemplateList as ContractTemplateList,
  TemplateEditor as ContractTemplateEditor,
  ContractList,
  ContractCreate,
  ContractDetail,
} from './pages/admin/contracts';
import {
  AssetList,
  AssetDetails,
  AssetOrigination,
  SupplyManagement,
  TokenConfiguration,
  YieldConfiguration,
  SmartContractTemplateList,
  SmartContractTemplateDetail,
  SmartContractTemplateCreate,
  TemplateAuditManagement,
  TenantTokenizationDashboard,
  VersionComparison,
  SecuritySettings as TokenizationSecuritySettings,
  FinancialHealthDashboard as AdminFinancialHealthDashboard,
  SecurityAuditLog,
  AuditListPage,
} from './pages/admin/tokenization';
import {
  // Crypto & DeFi pages
  BankingCrypto,
  BankingDefiPools,
  BankingDefiPositions,
  BankingCryptoWallets,
  BankingCryptoHistory,
  BankingCryptoMarketplace,
  // Customer pages
  CustomerDashboard,
  CustomerPix,
  CustomerPayments,
  CustomerTransfers,
  CustomerStatement,
  // Supplier pages
  SupplierDashboard,
  SupplierCharges,
  SupplierCustomers,
  SupplierWithdrawals,
  SupplierReports,
  // Employee pages
  EmployeeDashboard,
  EmployeeSalaryAdvance,
  EmployeeBenefits,
  EmployeePayslip,
  EmployeeHrRequests,
  // Shared pages
  AccountSettings,
  SecuritySettings,
  NotificationSettings,
  TwoFactorSetup,
  // Benefits pages
  Benefits,
  MySubscriptions,
  BenefitDetails,
  BenefitSubscribe,
  SubscriptionDetails,
  // Banking Expansion pages
  BillPayment,
  MobileTopup,
  PixSaque,
  BatchTransfer,
  RecurringTransfers,
  // Crypto Expansion pages
  Staking,
  NftGallery,
  Bridge,
  AdvancedSwap,
  YieldFarming,
  // KYC pages
  KycSubmission,
  KycStatus,
  // Tokenization pages (Correntista)
  TokenMarketplace,
  TokenDetails,
  BuyToken,
  SellToken,
  TokenPortfolio,
  OrderHistory,
  YieldHistory,
  RedeemToken,
  // Contract pages (Correntista)
  MyContracts,
  ContractSign,
  IdentityVerification,
  // Company PJ pages
  CompanyRegister,
  CompanyDocuments,
  CompanyStatus,
} from './pages/banking';
import {
  LoanSimulator,
  LoanRequest,
  MyLoans,
} from './pages/banking/credit';
import { DisputeCreate } from './pages/banking/disputes/DisputeCreate';
import { DisputeList } from './pages/banking/disputes/DisputeList';
import { DisputeDetail } from './pages/banking/disputes/DisputeDetail';
import { DisputeQueue } from './pages/admin/disputes/DisputeQueue';
import { DisputeReview } from './pages/admin/disputes/DisputeReview';
import {
  ConsentDashboard,
  ConsentDetail,
  MyCards,
  RequestCard,
  CardDetail,
  CardTransactions,
  CardInvoices,
  InvestmentMarketplace,
  MyPortfolio,
  InvestorProfile,
  InvestmentDetail,
  InvestmentSimulator,
  ScheduledPixList,
  ScheduledPixCreate,
  ScheduledPixDetail,
  MedRequestList,
  MedRequestCreate,
} from './pages/banking';
import { Recovery } from './pages/auth';
import { VerifyEmail } from './pages/auth/VerifyEmail';
import { ResetPassword } from './pages/ResetPassword';
import { ForcedPasswordReset } from './pages/ForcedPasswordReset';
import { ContractVerification } from './pages/public/ContractVerification';
import { InviteRegister } from './pages/InviteRegister';
import { InviteLinks } from './pages/admin/InviteLinks';
import { BankingRedirect, ProfileRoute } from './components/guards';
import { ModuleRoute } from './components/guards/ModuleRoute';
import { useAuthStore } from './stores/auth.store';
import { isPasswordResetRequired } from './lib/passwordResetFlag';
import './styles/immersive-themes.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Loading component
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-surface-400">Carregando...</p>
      </div>
    </div>
  );
}

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitialized } = useAuthStore();
  
  // Wait for initialization
  if (!isInitialized) {
    return <LoadingScreen />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Security Hardening Phase 1: force password change before any other route
  if (isPasswordResetRequired()) {
    return <Navigate to="/reset-password-forced" replace />;
  }

  return <>{children}</>;
}

// Authenticated route without the main layout (for blocking pages like forced password reset)
function AuthenticatedWithoutLayoutRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitialized } = useAuthStore();

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public Route wrapper (redirect if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitialized, getDefaultRoute } = useAuthStore();
  
  // Wait for initialization
  if (!isInitialized) {
    return <LoadingScreen />;
  }
  
  if (isAuthenticated) {
    // Redirect to appropriate dashboard based on role
    return <Navigate to={getDefaultRoute()} replace />;
  }
  
  return <>{children}</>;
}

// Super Admin Route wrapper
function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitialized, isSuperAdmin } = useAuthStore();
  
  if (!isInitialized) {
    return <LoadingScreen />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isSuperAdmin()) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

// Tenant Admin Route wrapper
function TenantAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitialized, user, isSuperAdmin, isCorrentista } = useAuthStore();
  
  if (!isInitialized) {
    return <LoadingScreen />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Super admins can access tenant admin routes
  if (isSuperAdmin()) {
    return <>{children}</>;
  }
  
  // Correntistas cannot access admin routes
  if (isCorrentista()) {
    return <Navigate to="/banking" replace />;
  }
  
  // Only ADMIN type can access admin routes
  if (user?.type !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

// Banking Route wrapper (for correntistas)
function BankingRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitialized, isCorrentista, isSuperAdmin } = useAuthStore();
  
  if (!isInitialized) {
    return <LoadingScreen />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Super admins and correntistas can access banking routes
  if (isSuperAdmin() || isCorrentista()) {
    return <>{children}</>;
  }
  
  // Non-correntistas are redirected to main dashboard
  return <Navigate to="/" replace />;
}

// App initializer component
function AppInitializer({ children }: { children: React.ReactNode }) {
  const { initialize, isInitialized } = useAuthStore();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const init = async () => {
      await initialize();
      setIsReady(true);
    };
    init();
  }, [initialize]);

  if (!isReady || !isInitialized) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ImmersiveThemeProvider>
        <BrowserRouter>
          <AppInitializer>
            <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/recovery"
              element={
                <PublicRoute>
                  <Recovery />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/verify-email"
              element={
                <PublicRoute>
                  <VerifyEmail />
                </PublicRoute>
              }
            />
            
            {/* Public Contract Verification Route */}
            <Route path="/verify/:contractId" element={<ContractVerification />} />

            {/* Admin Login Route (separate context with fixed tenant) */}
            <Route
              path="/admin-login"
              element={
                <PublicRoute>
                  <AdminLogin />
                </PublicRoute>
              }
            />

            {/* Public Invite Registration Route */}
            <Route path="/invite/:token" element={<InviteRegister />} />

            {/* Forced password reset (authenticated but no layout) */}
            <Route
              path="/reset-password-forced"
              element={
                <AuthenticatedWithoutLayoutRoute>
                  <ForcedPasswordReset />
                </AuthenticatedWithoutLayoutRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Dashboard />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/accounts" element={<Accounts />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/settings" element={<Settings />} />
              
              {/* Super Admin Routes */}
              <Route path="/super-admin" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
              <Route path="/super-admin/tenants" element={<SuperAdminRoute><SuperAdminTenants /></SuperAdminRoute>} />
              <Route path="/super-admin/users" element={<SuperAdminRoute><SuperAdminUsers /></SuperAdminRoute>} />
              <Route path="/super-admin/providers" element={<SuperAdminRoute><SuperAdminProviders /></SuperAdminRoute>} />
              <Route path="/super-admin/provider-templates" element={<SuperAdminRoute><SuperAdminProviderTemplates /></SuperAdminRoute>} />
              <Route path="/super-admin/monitoring" element={<SuperAdminRoute><Monitoring /></SuperAdminRoute>} />
              <Route path="/super-admin/transactions" element={<SuperAdminRoute><Transactions /></SuperAdminRoute>} />
              <Route path="/super-admin/revenue" element={<SuperAdminRoute><RevenueDashboard /></SuperAdminRoute>} />
              <Route path="/super-admin/revenue/tenants" element={<SuperAdminRoute><TenantRevenueList /></SuperAdminRoute>} />
              <Route path="/super-admin/platform-fees" element={<SuperAdminRoute><PlatformFeeConfig /></SuperAdminRoute>} />
              <Route path="/super-admin/fee-health" element={<SuperAdminRoute><FeeHealthCheck /></SuperAdminRoute>} />
              <Route path="/super-admin/provider-costs" element={<SuperAdminRoute><ProviderCosts /></SuperAdminRoute>} />
              <Route path="/super-admin/tokens" element={<SuperAdminRoute><TokenManagement /></SuperAdminRoute>} />
              <Route path="/super-admin/theming" element={<SuperAdminRoute><ThemeEditor /></SuperAdminRoute>} />
              <Route path="/super-admin/theming-advanced" element={<SuperAdminRoute><ThemeEditorAdvanced /></SuperAdminRoute>} />
              <Route path="/super-admin/facial-verification" element={<SuperAdminRoute><FacialVerificationRules /></SuperAdminRoute>} />
              <Route path="/super-admin/modules" element={<SuperAdminRoute><PlatformModules /></SuperAdminRoute>} />
              <Route path="/super-admin/financial-security" element={<SuperAdminRoute><FinancialSecuritySettings /></SuperAdminRoute>} />
              <Route path="/super-admin/financial-health" element={<SuperAdminRoute><FinancialHealthDashboard /></SuperAdminRoute>} />
              
              {/* Super Admin - Tokenization Routes */}
              <Route path="/super-admin/asset-types" element={<SuperAdminRoute><AssetTypesManagement /></SuperAdminRoute>} />
              <Route path="/super-admin/tokenization" element={<SuperAdminRoute><TokenizationDashboard /></SuperAdminRoute>} />
              <Route path="/super-admin/oracle" element={<SuperAdminRoute><OracleConfiguration /></SuperAdminRoute>} />
              <Route path="/super-admin/compliance-rules" element={<SuperAdminRoute><ComplianceRulesConfig /></SuperAdminRoute>} />
              <Route path="/super-admin/tenant-categories" element={<SuperAdminRoute><TenantCategoryAccess /></SuperAdminRoute>} />

              {/* Company PJ Super Admin Routes */}
              <Route path="/super-admin/companies" element={<SuperAdminRoute><CompanyListAll /></SuperAdminRoute>} />
              <Route path="/super-admin/companies/:id" element={<SuperAdminRoute><CompanyDetailAll /></SuperAdminRoute>} />

              {/* Tenant Admin Routes */}
              <Route path="/admin" element={<TenantAdminRoute><Dashboard /></TenantAdminRoute>} />
              <Route path="/admin/setup" element={<TenantAdminRoute><AdminSetup /></TenantAdminRoute>} />
              <Route path="/admin/tenants" element={<TenantAdminRoute><AdminTenants /></TenantAdminRoute>} />
              <Route path="/admin/users" element={<TenantAdminRoute><AdminUsers /></TenantAdminRoute>} />
              <Route path="/admin/invite-links" element={<TenantAdminRoute><InviteLinks /></TenantAdminRoute>} />
              <Route path="/admin/permissions" element={<TenantAdminRoute><AdminPermissions /></TenantAdminRoute>} />
              <Route path="/admin/audit" element={<TenantAdminRoute><AuditLogs /></TenantAdminRoute>} />
              <Route path="/admin/costs" element={<TenantAdminRoute><AdminCostsDashboard /></TenantAdminRoute>} />
              <Route path="/admin/partners" element={<TenantAdminRoute><AdminPartners /></TenantAdminRoute>} />
              <Route path="/admin/fees" element={<TenantAdminRoute><AdminFees /></TenantAdminRoute>} />
              <Route path="/admin/managers" element={<TenantAdminRoute><AdminManagers /></TenantAdminRoute>} />
              <Route path="/admin/entities" element={<TenantAdminRoute><AdminEntities /></TenantAdminRoute>} />
              <Route path="/admin/bank-connections" element={<TenantAdminRoute><AdminBankConnections /></TenantAdminRoute>} />
              <Route path="/admin/fee-rules" element={<TenantAdminRoute><AdminFeeRules /></TenantAdminRoute>} />
              <Route path="/admin/provider-templates" element={<TenantAdminRoute><ProviderTemplates /></TenantAdminRoute>} />
              <Route path="/admin/provider-templates/new" element={<TenantAdminRoute><TemplateWizard /></TenantAdminRoute>} />
              <Route path="/admin/provider-templates/:id/edit" element={<TenantAdminRoute><TemplateWizard /></TenantAdminRoute>} />
              <Route path="/admin/marketplace" element={<TenantAdminRoute><AdminMarketplace /></TenantAdminRoute>} />
              <Route path="/admin/provider-instances" element={<TenantAdminRoute><ProviderInstances /></TenantAdminRoute>} />
              <Route path="/admin/test-console" element={<TenantAdminRoute><TestConsole /></TenantAdminRoute>} />
              <Route path="/admin/resources" element={<TenantAdminRoute><ResourceConfig /></TenantAdminRoute>} />
              <Route path="/admin/fee-config" element={<TenantAdminRoute><FeeConfig /></TenantAdminRoute>} />
              <Route path="/admin/monitoring" element={<TenantAdminRoute><Monitoring /></TenantAdminRoute>} />
              <Route path="/admin/revenue" element={<TenantAdminRoute><RevenueDashboard /></TenantAdminRoute>} />
              <Route path="/admin/my-fees" element={<TenantAdminRoute><TenantFeeConfig /></TenantAdminRoute>} />
              <Route path="/admin/my-revenue" element={<TenantAdminRoute><TenantRevenueDashboard /></TenantAdminRoute>} />
              <Route path="/admin/benefits/plans" element={<TenantAdminRoute><ModuleRoute moduleId="BENEFITS"><BenefitPlans /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/benefits/subscriptions" element={<TenantAdminRoute><ModuleRoute moduleId="BENEFITS"><BenefitSubscriptions /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/benefits/dashboard" element={<TenantAdminRoute><ModuleRoute moduleId="BENEFITS"><BenefitsDashboard /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/reports" element={<TenantAdminRoute><AdminReports /></TenantAdminRoute>} />
              <Route path="/admin/economic-groups" element={<TenantAdminRoute><EconomicGroups /></TenantAdminRoute>} />
              
              {/* KYC Admin Routes */}
              <Route path="/admin/kyc" element={<TenantAdminRoute><KycQueue /></TenantAdminRoute>} />
              <Route path="/admin/kyc/dashboard" element={<TenantAdminRoute><KycDashboard /></TenantAdminRoute>} />
              <Route path="/admin/kyc/settings" element={<TenantAdminRoute><KycSettings /></TenantAdminRoute>} />
              <Route path="/admin/kyc/analysts" element={<TenantAdminRoute><KycAnalystManagement /></TenantAdminRoute>} />
              <Route path="/admin/kyc/templates" element={<TenantAdminRoute><KycTemplates /></TenantAdminRoute>} />
              <Route path="/admin/kyc/reports" element={<TenantAdminRoute><KycReports /></TenantAdminRoute>} />
              <Route path="/admin/kyc/:virtualAccountId" element={<TenantAdminRoute><KycReview /></TenantAdminRoute>} />
              
              {/* Facial Verification Rules */}
              <Route path="/admin/facial-verification" element={<TenantAdminRoute><FacialVerificationRules /></TenantAdminRoute>} />
              <Route path="/admin/facial-config" element={<TenantAdminRoute><FacialConfigPage /></TenantAdminRoute>} />
              
              {/* Tenant Modules */}
              <Route path="/admin/modules" element={<TenantAdminRoute><TenantModules /></TenantAdminRoute>} />
              
              {/* Advanced Security Routes */}
              <Route path="/admin/security/config" element={<TenantAdminRoute><AdvancedSecurityConfig /></TenantAdminRoute>} />
              <Route path="/admin/security/audit" element={<TenantAdminRoute><SecurityAuditLogs /></TenantAdminRoute>} />
              
              {/* System Health Monitoring */}
              <Route path="/admin/system-health" element={<TenantAdminRoute><SystemHealthDashboard /></TenantAdminRoute>} />
              
              {/* Immersive Themes */}
              <Route path="/admin/immersive-themes" element={<TenantAdminRoute><ImmersiveThemes /></TenantAdminRoute>} />
              <Route path="/admin/futuristic-layouts" element={<TenantAdminRoute><FuturisticLayoutGallery /></TenantAdminRoute>} />
              
              {/* Credit Engine Admin Routes */}
              <Route path="/admin/credit" element={<TenantAdminRoute><ModuleRoute moduleId="CREDIT_ENGINE"><CreditDashboard /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/credit/dashboard" element={<TenantAdminRoute><ModuleRoute moduleId="CREDIT_ENGINE"><CreditDashboard /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/credit/policies" element={<TenantAdminRoute><ModuleRoute moduleId="CREDIT_ENGINE"><CreditPolicies /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/credit/products" element={<TenantAdminRoute><ModuleRoute moduleId="CREDIT_ENGINE"><CreditProducts /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/credit/requests" element={<TenantAdminRoute><ModuleRoute moduleId="CREDIT_ENGINE"><LoanRequests /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/credit/loans" element={<TenantAdminRoute><ModuleRoute moduleId="CREDIT_ENGINE"><ActiveLoans /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/credit/collection" element={<TenantAdminRoute><ModuleRoute moduleId="CREDIT_ENGINE"><CollectionRules /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/credit/reports" element={<TenantAdminRoute><ModuleRoute moduleId="CREDIT_ENGINE"><CreditReports /></ModuleRoute></TenantAdminRoute>} />
              
              {/* Open Finance Admin Routes */}
              <Route path="/admin/open-finance/config" element={<TenantAdminRoute><ModuleRoute moduleId="OPEN_FINANCE"><OpenFinanceConfig /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/open-finance/monitor" element={<TenantAdminRoute><ModuleRoute moduleId="OPEN_FINANCE"><OpenFinanceMonitor /></ModuleRoute></TenantAdminRoute>} />
              
              {/* Card Issuing Admin Routes */}
              <Route path="/admin/cards/config" element={<TenantAdminRoute><ModuleRoute moduleId="CARD_ISSUING"><CardIssuingConfig /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/cards/management" element={<TenantAdminRoute><ModuleRoute moduleId="CARD_ISSUING"><CardManagement /></ModuleRoute></TenantAdminRoute>} />
              
              {/* Investments Admin Routes */}
              <Route path="/admin/investments/products" element={<TenantAdminRoute><ModuleRoute moduleId="INVESTMENTS"><InvestmentProductsAdmin /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/investments/config" element={<TenantAdminRoute><ModuleRoute moduleId="INVESTMENTS"><InvestmentConfig /></ModuleRoute></TenantAdminRoute>} />
              
              {/* Tokenization Admin Routes */}
              <Route path="/admin/tokenization" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><TenantTokenizationDashboard /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/assets" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><AssetList /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/assets/new" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><AssetOrigination /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/assets/:id" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><AssetDetails /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/assets/:id/edit" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><AssetOrigination /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/assets/:id/token-config" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><TokenConfiguration /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/supply" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><SupplyManagement /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/supply/:id" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><SupplyManagement /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/token-config" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><TokenConfiguration /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/token-config/:id" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><TokenConfiguration /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/yield" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><YieldConfiguration /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/yield/:id" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><YieldConfiguration /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/compliance" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><ComplianceQueue /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/compliance/:id" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><AssetReview /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/contracts" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><SmartContractTemplateList /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/contracts/audits" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><AuditListPage /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/contracts/new" element={<SuperAdminRoute><SmartContractTemplateCreate /></SuperAdminRoute>} />
              <Route path="/admin/tokenization/contracts/:id" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><SmartContractTemplateDetail /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/contracts/:id/compare" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><VersionComparison /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/contracts/:id/audit" element={<SuperAdminRoute><TemplateAuditManagement /></SuperAdminRoute>} />
              <Route path="/admin/tokenization/security-settings" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><TokenizationSecuritySettings /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/financial-health" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><AdminFinancialHealthDashboard /></ModuleRoute></TenantAdminRoute>} />
              <Route path="/admin/tokenization/security-audit" element={<TenantAdminRoute><ModuleRoute moduleId="TOKENIZATION"><SecurityAuditLog /></ModuleRoute></TenantAdminRoute>} />
              
              {/* Digital Contracts Admin Routes */}
              <Route path="/admin/contracts" element={<TenantAdminRoute><ContractsDashboard /></TenantAdminRoute>} />
              <Route path="/admin/contracts/list" element={<TenantAdminRoute><ContractList /></TenantAdminRoute>} />
              <Route path="/admin/contracts/new" element={<TenantAdminRoute><ContractCreate /></TenantAdminRoute>} />
              <Route path="/admin/contracts/:id" element={<TenantAdminRoute><ContractDetail /></TenantAdminRoute>} />
              <Route path="/admin/contracts/templates" element={<TenantAdminRoute><ContractTemplateList /></TenantAdminRoute>} />
              <Route path="/admin/contracts/templates/new" element={<TenantAdminRoute><ContractTemplateEditor /></TenantAdminRoute>} />
              <Route path="/admin/contracts/templates/:id" element={<TenantAdminRoute><ContractTemplateEditor /></TenantAdminRoute>} />
              
              {/* MED Compliance Routes */}
              <Route path="/admin/compliance/med" element={<TenantAdminRoute><MedQueue /></TenantAdminRoute>} />
              <Route path="/admin/compliance/med/:id" element={<TenantAdminRoute><MedReview /></TenantAdminRoute>} />
              
              {/* PLD Compliance Routes */}
              <Route path="/admin/compliance/pld" element={<TenantAdminRoute><PldDashboard /></TenantAdminRoute>} />
              <Route path="/admin/compliance/pld/alerts" element={<TenantAdminRoute><PldAlertList /></TenantAdminRoute>} />
              <Route path="/admin/compliance/pld/alerts/:id" element={<TenantAdminRoute><PldAlertDetail /></TenantAdminRoute>} />
              <Route path="/admin/compliance/pld/rules" element={<TenantAdminRoute><PldRuleList /></TenantAdminRoute>} />
              <Route path="/admin/compliance/pld/rules/new" element={<TenantAdminRoute><PldRuleCreate /></TenantAdminRoute>} />
              <Route path="/admin/compliance/pld/rules/:id" element={<TenantAdminRoute><PldRuleCreate /></TenantAdminRoute>} />
              <Route path="/admin/compliance/pld/config" element={<TenantAdminRoute><PldConfig /></TenantAdminRoute>} />
              
              {/* COAF Compliance Routes */}
              <Route path="/admin/compliance/coaf" element={<TenantAdminRoute><CoafDashboard /></TenantAdminRoute>} />
              <Route path="/admin/compliance/coaf/list" element={<TenantAdminRoute><CoafCommunicationList /></TenantAdminRoute>} />
              <Route path="/admin/compliance/coaf/new" element={<TenantAdminRoute><CoafCommunicationCreate /></TenantAdminRoute>} />
              <Route path="/admin/compliance/coaf/:id" element={<TenantAdminRoute><CoafCommunicationDetail /></TenantAdminRoute>} />
              
              {/* Compliance Config Route */}
              <Route path="/admin/compliance/config" element={<TenantAdminRoute><ComplianceConfig /></TenantAdminRoute>} />
              
              {/* AnyPayBR Dashboard */}
              <Route path="/admin/anypay" element={<SuperAdminRoute><AnypayDashboard /></SuperAdminRoute>} />
              <Route path="/admin/anypay/company-register" element={<SuperAdminRoute><CompanyRegistration /></SuperAdminRoute>} />
              
              {/* Membership Fees */}
              <Route path="/admin/membership-fees" element={<TenantAdminRoute><MembershipFeesPage /></TenantAdminRoute>} />

              {/* Company PJ Admin Routes */}
              <Route path="/admin/companies" element={<TenantAdminRoute><CompanyList /></TenantAdminRoute>} />
              <Route path="/admin/companies/:id" element={<TenantAdminRoute><CompanyDetail /></TenantAdminRoute>} />
              
              {/* Financial Overview */}
              <Route path="/admin/financial-overview" element={<SuperAdminRoute><FinancialOverviewPage /></SuperAdminRoute>} />
              <Route path="/admin/financial-summary" element={<TenantAdminRoute><FinancialSummaryPage /></TenantAdminRoute>} />
              
              {/* Reconciliation */}
              <Route path="/admin/reconciliation" element={<TenantAdminRoute><ReconciliationPage /></TenantAdminRoute>} />

              {/* Security Config (Tenant Admin) */}
              <Route path="/admin/security-config" element={<TenantAdminRoute><SecurityConfigPage /></TenantAdminRoute>} />
              
              {/* Payment Dispute Admin Routes */}
              <Route path="/admin/disputes" element={<TenantAdminRoute><DisputeQueue /></TenantAdminRoute>} />
              <Route path="/admin/disputes/:id" element={<TenantAdminRoute><DisputeReview /></TenantAdminRoute>} />
              
              {/* Financial Reports */}
              <Route path="/admin/reports/hub" element={<TenantAdminRoute><ReportsHubPage /></TenantAdminRoute>} />
              <Route path="/admin/reports/consolidated" element={<TenantAdminRoute><ConsolidatedReportPage /></TenantAdminRoute>} />
              <Route path="/admin/reports/per-customer" element={<TenantAdminRoute><PerCustomerReportPage /></TenantAdminRoute>} />
              <Route path="/admin/reports/fees-membership" element={<TenantAdminRoute><FeesMembershipReportPage /></TenantAdminRoute>} />
              <Route path="/admin/reports/detailed-transactions" element={<TenantAdminRoute><DetailedTransactionsReportPage /></TenantAdminRoute>} />
              
              {/* Internet Banking Routes */}
              {/* Base route - redirects to profile-specific dashboard */}
              <Route path="/banking" element={<BankingRoute><BankingRedirect /></BankingRoute>} />
              
              {/* Customer Routes */}
              <Route path="/banking/customer" element={<BankingRoute><ProfileRoute allowedTypes={['CUSTOMER']}><CustomerDashboard /></ProfileRoute></BankingRoute>} />
              <Route path="/banking/customer/pix" element={<BankingRoute><ModuleRoute moduleId="CORE_BANKING" featureKey="pixEnabled"><ProfileRoute allowedTypes={['CUSTOMER', 'EMPLOYEE']}><CustomerPix /></ProfileRoute></ModuleRoute></BankingRoute>} />
              <Route path="/banking/customer/payments" element={<BankingRoute><ProfileRoute allowedTypes={['CUSTOMER']}><CustomerPayments /></ProfileRoute></BankingRoute>} />
              <Route path="/banking/customer/transfers" element={<BankingRoute><ModuleRoute moduleId="CORE_BANKING" featureKey="transferEnabled"><ProfileRoute allowedTypes={['CUSTOMER', 'EMPLOYEE']}><CustomerTransfers /></ProfileRoute></ModuleRoute></BankingRoute>} />
              <Route path="/banking/customer/statement" element={<BankingRoute><ModuleRoute moduleId="CORE_BANKING" featureKey="statementEnabled"><ProfileRoute allowedTypes={['CUSTOMER', 'EMPLOYEE']}><CustomerStatement /></ProfileRoute></ModuleRoute></BankingRoute>} />
              
              {/* Supplier Routes */}
              <Route path="/banking/supplier" element={<BankingRoute><ProfileRoute allowedTypes={['SUPPLIER']}><SupplierDashboard /></ProfileRoute></BankingRoute>} />
              <Route path="/banking/supplier/charges" element={<BankingRoute><ProfileRoute allowedTypes={['SUPPLIER']}><SupplierCharges /></ProfileRoute></BankingRoute>} />
              <Route path="/banking/supplier/customers" element={<BankingRoute><ProfileRoute allowedTypes={['SUPPLIER']}><SupplierCustomers /></ProfileRoute></BankingRoute>} />
              <Route path="/banking/supplier/withdrawals" element={<BankingRoute><ProfileRoute allowedTypes={['SUPPLIER']}><SupplierWithdrawals /></ProfileRoute></BankingRoute>} />
              <Route path="/banking/supplier/reports" element={<BankingRoute><ProfileRoute allowedTypes={['SUPPLIER']}><SupplierReports /></ProfileRoute></BankingRoute>} />
              
              {/* Employee Routes */}
              <Route path="/banking/employee" element={<BankingRoute><ProfileRoute allowedTypes={['EMPLOYEE']}><EmployeeDashboard /></ProfileRoute></BankingRoute>} />
              <Route path="/banking/employee/salary-advance" element={<BankingRoute><ProfileRoute allowedTypes={['EMPLOYEE']}><EmployeeSalaryAdvance /></ProfileRoute></BankingRoute>} />
              <Route path="/banking/employee/benefits" element={<BankingRoute><ModuleRoute moduleId="BENEFITS"><ProfileRoute allowedTypes={['EMPLOYEE']}><EmployeeBenefits /></ProfileRoute></ModuleRoute></BankingRoute>} />
              <Route path="/banking/employee/payslip" element={<BankingRoute><ProfileRoute allowedTypes={['EMPLOYEE']}><EmployeePayslip /></ProfileRoute></BankingRoute>} />
              <Route path="/banking/employee/hr-requests" element={<BankingRoute><ProfileRoute allowedTypes={['EMPLOYEE']}><EmployeeHrRequests /></ProfileRoute></BankingRoute>} />
              
              {/* Shared Banking Routes (all profiles) */}
              <Route path="/banking/settings" element={<BankingRoute><AccountSettings /></BankingRoute>} />
              <Route path="/banking/settings/security" element={<BankingRoute><SecuritySettings /></BankingRoute>} />
              <Route path="/banking/security" element={<BankingRoute><SecuritySettings /></BankingRoute>} />
              <Route path="/banking/security/2fa" element={<BankingRoute><TwoFactorSetup /></BankingRoute>} />
              <Route path="/banking/notifications" element={<BankingRoute><NotificationSettings /></BankingRoute>} />
              
              {/* Crypto & DeFi Routes (all profiles) */}
              <Route path="/banking/crypto" element={<BankingRoute><ModuleRoute moduleId="CRYPTO"><BankingCrypto /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/crypto/wallets" element={<BankingRoute><ModuleRoute moduleId="CRYPTO"><BankingCryptoWallets /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/crypto/history" element={<BankingRoute><ModuleRoute moduleId="CRYPTO"><BankingCryptoHistory /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/crypto/marketplace" element={<BankingRoute><ModuleRoute moduleId="CRYPTO"><BankingCryptoMarketplace /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/defi/pools" element={<BankingRoute><ModuleRoute moduleId="CRYPTO"><BankingDefiPools /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/defi/positions" element={<BankingRoute><ModuleRoute moduleId="CRYPTO"><BankingDefiPositions /></ModuleRoute></BankingRoute>} />
              
              {/* Benefits Routes (all profiles) */}
              <Route path="/banking/benefits" element={<BankingRoute><ModuleRoute moduleId="BENEFITS"><Benefits /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/benefits/:planId" element={<BankingRoute><ModuleRoute moduleId="BENEFITS"><BenefitDetails /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/benefits/subscribe/:planId" element={<BankingRoute><ModuleRoute moduleId="BENEFITS"><BenefitSubscribe /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/my-subscriptions" element={<BankingRoute><ModuleRoute moduleId="BENEFITS"><MySubscriptions /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/my-subscriptions/:id" element={<BankingRoute><ModuleRoute moduleId="BENEFITS"><SubscriptionDetails /></ModuleRoute></BankingRoute>} />
              
              {/* Banking Expansion Routes (all profiles) */}
              <Route path="/banking/bills" element={<BankingRoute><ModuleRoute moduleId="CORE_BANKING" featureKey="boletoEnabled"><BillPayment /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/topup" element={<BankingRoute><MobileTopup /></BankingRoute>} />
              <Route path="/banking/pix-saque" element={<BankingRoute><ModuleRoute moduleId="CORE_BANKING" featureKey="pixEnabled"><PixSaque /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/batch-transfer" element={<BankingRoute><ModuleRoute moduleId="CORE_BANKING" featureKey="transferEnabled"><BatchTransfer /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/recurring" element={<BankingRoute><ModuleRoute moduleId="CORE_BANKING" featureKey="transferEnabled"><RecurringTransfers /></ModuleRoute></BankingRoute>} />
              
              {/* PIX Scheduler Routes */}
              <Route path="/banking/pix/scheduled" element={<BankingRoute><ModuleRoute moduleId="CORE_BANKING" featureKey="pixScheduledEnabled"><ScheduledPixList /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/pix/scheduled/new" element={<BankingRoute><ModuleRoute moduleId="CORE_BANKING" featureKey="pixScheduledEnabled"><ScheduledPixCreate /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/pix/scheduled/:id" element={<BankingRoute><ModuleRoute moduleId="CORE_BANKING" featureKey="pixScheduledEnabled"><ScheduledPixDetail /></ModuleRoute></BankingRoute>} />
              
              {/* MED Routes (Customer) */}
              <Route path="/banking/med" element={<BankingRoute><ModuleRoute moduleId="CORE_BANKING" featureKey="pixEnabled"><MedRequestList /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/med/new" element={<BankingRoute><ModuleRoute moduleId="CORE_BANKING" featureKey="pixEnabled"><MedRequestCreate /></ModuleRoute></BankingRoute>} />
              
              {/* Crypto Expansion Routes (all profiles) */}
              <Route path="/banking/staking" element={<BankingRoute><ModuleRoute moduleId="CRYPTO"><Staking /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/nft" element={<BankingRoute><ModuleRoute moduleId="CRYPTO"><NftGallery /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/bridge" element={<BankingRoute><ModuleRoute moduleId="CRYPTO"><Bridge /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/swap" element={<BankingRoute><ModuleRoute moduleId="CRYPTO"><AdvancedSwap /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/farming" element={<BankingRoute><ModuleRoute moduleId="CRYPTO"><YieldFarming /></ModuleRoute></BankingRoute>} />
              
              {/* Company PJ Routes (Banking) */}
              <Route path="/banking/company/register" element={<BankingRoute><CompanyRegister /></BankingRoute>} />
              <Route path="/banking/company/documents" element={<BankingRoute><CompanyDocuments /></BankingRoute>} />
              <Route path="/banking/company/status" element={<BankingRoute><CompanyStatus /></BankingRoute>} />

              {/* Payment Dispute Routes (Banking) */}
              <Route path="/banking/disputes" element={<BankingRoute><DisputeList /></BankingRoute>} />
              <Route path="/banking/disputes/new" element={<BankingRoute><DisputeCreate /></BankingRoute>} />
              <Route path="/banking/disputes/:id" element={<BankingRoute><DisputeDetail /></BankingRoute>} />

              {/* KYC Routes (all profiles) */}
              <Route path="/banking/kyc" element={<BankingRoute><KycSubmission /></BankingRoute>} />
              <Route path="/banking/kyc/status" element={<BankingRoute><KycStatus /></BankingRoute>} />
              <Route path="/banking/kyc/verify" element={<BankingRoute><IdentityVerification /></BankingRoute>} />
              
              {/* Tokenization Routes (Correntista) */}
              <Route path="/banking/tokens" element={<BankingRoute><ModuleRoute moduleId="TOKENIZATION"><TokenMarketplace /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/tokens/:id" element={<BankingRoute><ModuleRoute moduleId="TOKENIZATION"><TokenDetails /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/tokens/:id/buy" element={<BankingRoute><ModuleRoute moduleId="TOKENIZATION"><BuyToken /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/tokens/:id/sell" element={<BankingRoute><ModuleRoute moduleId="TOKENIZATION"><SellToken /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/tokens/:id/redeem" element={<BankingRoute><ModuleRoute moduleId="TOKENIZATION"><RedeemToken /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/portfolio" element={<BankingRoute><ModuleRoute moduleId="TOKENIZATION"><TokenPortfolio /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/orders" element={<BankingRoute><ModuleRoute moduleId="TOKENIZATION"><OrderHistory /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/yields" element={<BankingRoute><ModuleRoute moduleId="TOKENIZATION"><YieldHistory /></ModuleRoute></BankingRoute>} />
              
              {/* Digital Contracts Routes (Correntista) */}
              <Route path="/banking/contracts" element={<BankingRoute><MyContracts /></BankingRoute>} />
              <Route path="/banking/contracts/:id/sign" element={<BankingRoute><ContractSign /></BankingRoute>} />
              
              {/* Credit Engine Routes (Correntista) */}
              <Route path="/banking/credit/simulate" element={<BankingRoute><ModuleRoute moduleId="CREDIT_ENGINE"><LoanSimulator /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/credit/request" element={<BankingRoute><ModuleRoute moduleId="CREDIT_ENGINE"><LoanRequest /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/credit/loans" element={<BankingRoute><ModuleRoute moduleId="CREDIT_ENGINE"><MyLoans /></ModuleRoute></BankingRoute>} />
              
              {/* Open Finance Routes (Correntista) */}
              <Route path="/banking/consents" element={<BankingRoute><ModuleRoute moduleId="OPEN_FINANCE"><ConsentDashboard /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/consents/:id" element={<BankingRoute><ModuleRoute moduleId="OPEN_FINANCE"><ConsentDetail /></ModuleRoute></BankingRoute>} />
              
              {/* Card Issuing Routes (Correntista) */}
              <Route path="/banking/cards" element={<BankingRoute><ModuleRoute moduleId="CARD_ISSUING"><MyCards /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/cards/request" element={<BankingRoute><ModuleRoute moduleId="CARD_ISSUING"><RequestCard /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/cards/:id" element={<BankingRoute><ModuleRoute moduleId="CARD_ISSUING"><CardDetail /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/cards/:id/transactions" element={<BankingRoute><ModuleRoute moduleId="CARD_ISSUING"><CardTransactions /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/cards/:id/invoices" element={<BankingRoute><ModuleRoute moduleId="CARD_ISSUING"><CardInvoices /></ModuleRoute></BankingRoute>} />
              
              {/* Investments Routes (Correntista) */}
              <Route path="/banking/investments" element={<BankingRoute><ModuleRoute moduleId="INVESTMENTS"><InvestmentMarketplace /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/investments/portfolio" element={<BankingRoute><ModuleRoute moduleId="INVESTMENTS"><MyPortfolio /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/investments/profile" element={<BankingRoute><ModuleRoute moduleId="INVESTMENTS"><InvestorProfile /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/investments/simulate" element={<BankingRoute><ModuleRoute moduleId="INVESTMENTS"><InvestmentSimulator /></ModuleRoute></BankingRoute>} />
              <Route path="/banking/investments/:id" element={<BankingRoute><ModuleRoute moduleId="INVESTMENTS"><InvestmentDetail /></ModuleRoute></BankingRoute>} />
              
              {/* Manager Routes */}
              <Route path="/manager" element={<TenantAdminRoute><ManagerDashboard /></TenantAdminRoute>} />
              <Route path="/manager/approvals" element={<TenantAdminRoute><ManagerApprovals /></TenantAdminRoute>} />
              <Route path="/manager/commissions" element={<TenantAdminRoute><ManagerCommissions /></TenantAdminRoute>} />
              <Route path="/manager/users" element={<TenantAdminRoute><ManagerUsers /></TenantAdminRoute>} />
              
              {/* User Routes */}
              <Route path="/user/limits" element={<BankingRoute><UserLimits /></BankingRoute>} />
              <Route path="/user/approvals" element={<BankingRoute><UserMyApprovals /></BankingRoute>} />
              
              {/* Virtual Accounts & Crypto Routes */}
              <Route path="/virtual-accounts" element={<TenantAdminRoute><VirtualAccounts /></TenantAdminRoute>} />
              <Route path="/crypto" element={<BankingRoute><Crypto /></BankingRoute>} />
              <Route path="/defi-pools" element={<BankingRoute><DefiPools /></BankingRoute>} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AppInitializer>
        <InstallPrompt />
      </BrowserRouter>
      </ImmersiveThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
